import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile, KeycloakTokenParsed } from 'keycloak-js';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserModel } from '../_models/user.model';

const API_USERS_URL = `${environment.apiUrl}/core/users`;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private unsubscribe: Subscription[] = [];

  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  public getLoggedUser(): KeycloakTokenParsed | undefined {
    try {
      const userDetails: KeycloakTokenParsed | undefined = this.keycloakService.getKeycloakInstance()
        .idTokenParsed;
      return userDetails;
    } catch (e) {
      console.error("Exception", e);
      return undefined;
    }
  }

  /**
   * `ecotx_sid` do access token: o id da pessoa nos serviços da Datlaz (user-profile, payments). Não é o `sub` do
   * Keycloak; sem a claim, undefined.
   */
  public getEcotxSid(): string | undefined {
    try {
      const sid = this.keycloakService.getKeycloakInstance()?.tokenParsed?.['ecotx_sid'];
      return typeof sid === 'string' && sid ? sid : undefined;
    } catch {
      return undefined;
    }
  }

  public getLoggedUserAccessToken(): string | undefined {
    try {
      return this.keycloakService.getKeycloakInstance().token;
    } catch (e) {
      return undefined;
    }
  }

  getMe(): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.http.get<UserModel>(`${API_USERS_URL}/me`).pipe(
      map((user: UserModel) => {
        if (user) {
          this.currentUserSubject.next(user)
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  public isLoggedIn(): Promise<boolean> {
    return this.keycloakService.isLoggedIn();
  }

  public loadUserProfile(): Promise<KeycloakProfile> {
    return this.keycloakService.loadUserProfile();
  }

  public login(): void {
    this.keycloakService.login();
  }

  public logout(): void {
    this.keycloakService.logout(window.location.origin);
  }

  public redirectToProfile(): void {
    this.keycloakService.getKeycloakInstance().accountManagement();
  }

  public getRoles(): string[] {
    return this.keycloakService.getUserRoles();
  }

}
